import type { EtapaEscopo } from '@/hooks/inventario';

/**
 * O semáforo do inventário — uma regra só para o cartão, a gaveta e o mapa.
 *
 *   cinza     planejando (nenhum escopo congelado) ou sem escopo
 *   vermelho  menos da metade dos endereços COM SALDO bipados na 1ª contagem
 *   âmbar     da metade a 95%
 *   verde     95% ou mais, ou todos os escopos integrados
 *
 * O alvo são os endereços com saldo na foto do congelamento. O coletor não
 * registra endereço vazio, e o fechamento do PBS trata vazio sem bipe como 0
 * contra 0 — medir sobre todos os endereços nunca chegaria a 100% (o escopo
 * 2127 terminou com 81% do que tinha saldo, e 31% do total).
 *
 * "Parado" é à parte: escopo em contagem sem ENVIO do coletor há mais de 2
 * horas. O app só envia quando a pessoa toca em Encerrar (tudo fica no
 * aparelho até lá), então "parado" quer dizer "ninguém encerrou", não
 * necessariamente "ninguém está contando". É o
 * que mais se pergunta na operação ("estão contando?"), e o percentual sozinho
 * não responde — 60% pode ser bom às 10h e ruim às 17h.
 */
export type Tom = 'neutro' | 'erro' | 'alerta' | 'ok';

export interface Semaforo {
  tom: Tom;
  rotulo: string;
  pct: number;
  parado: boolean;
}

const HORAS_PARADO = 2;

export function semaforoDoInventario(i: {
  escopos: number;
  localizadores: number;
  coletados: number;
  integrados: number;
  alvo: number;
  bipadosAlvo: number;
  etapas: Partial<Record<EtapaEscopo, number>>;
  ultimaColeta: string | null;
}): Semaforo {
  const pct = i.alvo > 0 ? Math.min(100, Math.round((i.bipadosAlvo / i.alvo) * 100)) : 0;
  const emContagem = (i.etapas.contagem ?? 0) + (i.etapas.recontagem ?? 0) + (i.etapas.final ?? 0);
  const parado =
    emContagem > 0 &&
    (i.ultimaColeta === null || Date.now() - new Date(i.ultimaColeta).getTime() > HORAS_PARADO * 3_600_000);

  if (i.escopos === 0) return { tom: 'neutro', rotulo: 'Sem escopo', pct, parado: false };
  if (i.integrados === i.escopos) return { tom: 'ok', rotulo: 'Integrado', pct: 100, parado: false };
  if ((i.etapas.montagem ?? 0) === i.escopos) return { tom: 'neutro', rotulo: 'Planejando', pct, parado: false };
  if (pct >= 95) return { tom: 'ok', rotulo: `${pct}% bipado`, pct, parado };
  if (pct >= 50) return { tom: 'alerta', rotulo: `${pct}% bipado`, pct, parado };
  return { tom: 'erro', rotulo: `${pct}% bipado`, pct, parado };
}

/**
 * O % bipado de UM escopo (ou etapa) e a cor dele — as mesmas faixas do
 * semáforo do inventário, para escopo e inventário falarem a mesma língua.
 */
export function percentualBipado(feitos: number, alvo: number): { pct: number; tom: Tom } {
  const pct = alvo > 0 ? Math.min(100, Math.round((feitos / alvo) * 100)) : 0;
  return { pct, tom: alvo === 0 ? 'neutro' : pct >= 95 ? 'ok' : pct >= 50 ? 'alerta' : 'erro' };
}

export const COR_TOM: Record<Tom, { ponto: string; barra: string; texto: string; fundo: string }> = {
  neutro: { ponto: 'bg-bordaForte', barra: 'bg-bordaForte', texto: 'text-textoFraco', fundo: 'bg-superficie2' },
  erro: {
    ponto: 'bg-status-divergencia',
    barra: 'bg-status-divergencia',
    texto: 'text-status-divergencia',
    fundo: 'bg-status-divergenciaFundo',
  },
  alerta: {
    ponto: 'bg-status-atencao',
    barra: 'bg-status-atencao',
    texto: 'text-status-atencao',
    fundo: 'bg-status-atencaoFundo',
  },
  ok: { ponto: 'bg-status-livre', barra: 'bg-status-livre', texto: 'text-status-livre', fundo: 'bg-status-livreFundo' },
};

/** "há 5 min", "há 3 h", "há 2 dias". */
export function haQuanto(iso: string | null): string {
  if (!iso) return 'nunca';
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.round(h / 24)} dias`;
}

export const ROTULO_ETAPA_CURTO: Record<EtapaEscopo, string> = {
  montagem: 'Planejamento',
  contagem: 'Contagem',
  recontagem: 'Recontagem',
  final: 'Final',
  integracao: 'A integrar',
  integrado: 'Integrado',
};
